import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Brain, Users, Settings, ArrowRight, Activity, Layers } from 'lucide-react'
import { PageHeader, fadeUp, staggerContainer, staggerItem } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { usePersonaEngineHealth, usePersonaEngineEngines } from '@/hooks/usePersonaEngine'
import type { EngineType } from '@personic/shared/types/persona-engine'
import { ENGINE_LABELS } from '@personic/shared/types/persona-engine'

const navCards = [
  {
    label: 'Archetype Library',
    href: '/admin/persona-engine/archetypes',
    icon: Users,
    description: 'Browse and manage 27 v3.0 archetypes across Identity and Engage products',
  },
  {
    label: 'Role Templates',
    href: '/admin/persona-engine/roles',
    icon: Layers,
    description: 'Manage Engage B2B role templates with dimension nudges',
  },
  {
    label: 'Graduation Dashboard',
    href: '/admin/persona-engine/graduation',
    icon: Activity,
    description: 'Monitor persona graduation stages and dimension derivation',
  },
  {
    label: 'Engine Config',
    href: '/admin/persona-engine/config',
    icon: Settings,
    description: 'Configure engine selection, thresholds, and modifier stack',
  },
]

function HealthSkeleton() {
  return (
    <div className="rounded-xl border border-warm-200 bg-white p-6 animate-pulse">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-warm-100" />
        <div className="space-y-2">
          <div className="h-4 w-40 bg-warm-100 rounded" />
          <div className="h-3 w-24 bg-warm-100 rounded" />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-20 bg-warm-100 rounded" />
            <div className="h-5 w-12 bg-warm-100 rounded" />
          </div>
        ))}
      </div>
    </div>
  )
}

function EngineSkeleton() {
  return (
    <div className="rounded-xl border border-warm-200 bg-white p-6 animate-pulse">
      <div className="h-4 w-32 bg-warm-100 rounded mb-4" />
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="h-3 w-48 bg-warm-100 rounded" />
            <div className="h-5 w-16 bg-warm-100 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  )
}

export default function PersonaEngineAdminDashboard() {
  const breadcrumbs = buildBreadcrumbs('/admin/persona-engine')
  const { data: health, isLoading: healthLoading } = usePersonaEngineHealth()
  const { data: engines, isLoading: enginesLoading } = usePersonaEngineEngines()

  const isHealthy = health?.status === 'healthy'

  const availableEngineSet = new Set<EngineType>(health?.engines_available ?? [])

  return (
    <div>
      <PageHeader
        title="Persona Engine v3.0"
        description="Persona Engine administration and monitoring."
        breadcrumbs={breadcrumbs}
      />

      <div className="max-w-5xl space-y-6">
        {/* Health Status Card */}
        {healthLoading ? (
          <HealthSkeleton />
        ) : health ? (
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <div className="rounded-xl border border-warm-200 bg-white p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-copper-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="font-heading font-semibold text-midnight-800">System Health</h2>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                        isHealthy
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-red-50 text-red-700'
                      }`}
                    >
                      {isHealthy ? 'Healthy' : 'Unhealthy'}
                    </span>
                  </div>
                  <p className="text-xs text-warm-500">Version {health.version}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-warm-500 mb-1">Engines Available</p>
                  <p className="text-lg font-semibold text-midnight-800">{health.engines_available.length}</p>
                </div>
                <div>
                  <p className="text-xs text-warm-500 mb-1">Archetypes</p>
                  <p className="text-lg font-semibold text-midnight-800">{health.archetype_count}</p>
                </div>
                <div>
                  <p className="text-xs text-warm-500 mb-1">Role Templates</p>
                  <p className="text-lg font-semibold text-midnight-800">{health.role_template_count}</p>
                </div>
                <div>
                  <p className="text-xs text-warm-500 mb-1">Features</p>
                  <div className="flex flex-wrap gap-1.5 mt-0.5">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        health.graduation_enabled
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-warm-100 text-warm-500'
                      }`}
                    >
                      Graduation
                    </span>
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        health.instances_enabled
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-warm-100 text-warm-500'
                      }`}
                    >
                      Instances
                    </span>
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        health.roles_enabled
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-warm-100 text-warm-500'
                      }`}
                    >
                      Roles
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ) : null}

        {/* Quick Navigation Grid */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          {navCards.map((card) => (
            <motion.div key={card.href} variants={staggerItem}>
              <Link
                to={card.href}
                className="flex items-start justify-between p-5 rounded-xl border border-warm-200 bg-white hover:border-copper-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center flex-shrink-0 group-hover:bg-copper-100 transition-colors">
                    <card.icon className="w-5 h-5 text-copper-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-midnight-800 group-hover:text-copper-700">
                      {card.label}
                    </p>
                    <p className="text-xs text-warm-500 mt-0.5 leading-relaxed">{card.description}</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-warm-300 group-hover:text-copper-500 flex-shrink-0 mt-1" />
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Engine Status List */}
        {enginesLoading ? (
          <EngineSkeleton />
        ) : engines && engines.length > 0 ? (
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <div className="rounded-xl border border-warm-200 bg-white p-6">
              <h2 className="font-heading font-semibold text-midnight-800 mb-4">Engine Status</h2>
              <div className="space-y-3">
                {engines.map((engine) => {
                  const available = availableEngineSet.has(engine.engine_name)
                  return (
                    <div
                      key={engine.engine_name}
                      className="flex items-center justify-between py-2 border-b border-warm-100 last:border-0"
                    >
                      <div>
                        <p className="text-sm font-medium text-midnight-800">
                          {ENGINE_LABELS[engine.engine_name] ?? engine.engine_name}
                        </p>
                        <p className="text-xs text-warm-500">{engine.description}</p>
                      </div>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                          available
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-warm-100 text-warm-500'
                        }`}
                      >
                        {available ? 'Available' : 'Unavailable'}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </motion.div>
        ) : null}
      </div>
    </div>
  )
}
